import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  runTransaction,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { 
  Hotel, 
  WebUser, 
  HotelCode, 
  HotelUser, 
  HotelZone,
  CreateHotelRequest,
  CreateWebUserRequest,
  CreateHotelUserRequest,
  Subscription,
  Payment,
  BillingHistory
} from '../types/Firebase';
// Password is now stored as plain text for hotel initialization
// This allows easy sync with local hotel backend systems

// Lazy initialization to avoid import-time Firebase access
function getDb() {
  return getFirestore();
}

// Collection references - lazily initialized
function getHotelsCollection() {
  return collection(getDb(), 'hotels');
}

function getWebUsersCollection() {
  return collection(getDb(), 'webUsers');
}

function getHotelCodesCollection() {
  return collection(getDb(), 'hotelCodes');
}

function getSubscriptionsCollection() {
  return collection(getDb(), 'subscriptions');
}

function getPaymentsCollection() {
  return collection(getDb(), 'payments');
}

function getBillingHistoryCollection() {
  return collection(getDb(), 'billingHistory');
}

// ============= HOTEL CODES =============

export class HotelCodeService {
  /**
   * Generate a 6-digit hotel code
   */
  static generateCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Check if a hotel code is available
   */
  static async isCodeAvailable(code: string): Promise<boolean> {
    try {
      const codeDoc = await getDoc(doc(getHotelCodesCollection(), code));
      return !codeDoc.exists();
    } catch (error) {
      console.error('Error checking code availability:', error);
      return false;
    }
  }

  /**
   * Generate a unique hotel code with retries
   */
  static async generateUniqueCode(maxRetries: number = 10): Promise<string> {
    for (let i = 0; i < maxRetries; i++) {
      const code = this.generateCode();
      const isAvailable = await this.isCodeAvailable(code);
      
      if (isAvailable) {
        return code;
      }
    }
    throw new Error(`Failed to generate unique hotel code after ${maxRetries} attempts`);
  }

  /**
   * Reserve a hotel code
   */
  static async reserveCode(code: string, webUserId?: string): Promise<void> {
    const hotelCodeData: HotelCode = {
      id: code,
      reserved: true,
      reservedAt: new Date(),
      assignedTo: webUserId,
      createdAt: new Date()
    };

    await setDoc(doc(getHotelCodesCollection(), code), hotelCodeData);
  }

  /**
   * Assign code to hotel
   */
  static async assignCodeToHotel(code: string, hotelId: string): Promise<void> {
    await updateDoc(doc(getHotelCodesCollection(), code), {
      hotelId: hotelId,
      updatedAt: new Date()
    });
  }

  /**
   * Release a hotel code
   */
  static async releaseCode(code: string): Promise<void> {
    await deleteDoc(doc(getHotelCodesCollection(), code));
  }
}

// ============= HOTELS =============

export class HotelService {
  /**
   * Create a new hotel
   */
  static async createHotel(hotelData: CreateHotelRequest, hotelCode: string): Promise<Hotel> {
    return runTransaction(getDb(), async (transaction) => {
      // Verify hotel code is reserved
      const codeRef = doc(getHotelCodesCollection(), hotelCode);
      const codeDoc = await transaction.get(codeRef);
      
      if (!codeDoc.exists()) {
        throw new Error('Hotel code not found or not reserved');
      }

      // Create hotel document
      const hotelRef = doc(getHotelsCollection());
      const hotel: Hotel = {
        id: hotelRef.id,
        hotelCode: hotelCode,
        name: hotelData.name,
        address: hotelData.address,
        serverUrl: hotelData.serverUrl,
        ...(hotelData.serverPort && { serverPort: hotelData.serverPort }),
        isAdminInstance: hotelData.isAdminInstance || false,
        status: 'offline',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      transaction.set(hotelRef, hotel);

      // Update hotel code to reference this hotel
      transaction.update(codeRef, {
        hotelId: hotel.id,
        updatedAt: new Date()
      });

      return hotel;
    });
  }

  /**
   * Get hotel by code
   */
  static async getHotelByCode(hotelCode: string): Promise<Hotel | null> {
    try {
      const q = query(getHotelsCollection(), where('hotelCode', '==', hotelCode));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Hotel;
    } catch (error) {
      console.error('Error getting hotel by code:', error);
      return null;
    }
  }

  /**
   * Get hotel by ID
   */
  static async getHotelById(hotelId: string): Promise<Hotel | null> {
    try {
      const hotelDoc = await getDoc(doc(getHotelsCollection(), hotelId));
      
      if (!hotelDoc.exists()) {
        return null;
      }

      return { id: hotelDoc.id, ...hotelDoc.data() } as Hotel;
    } catch (error) {
      console.error('Error getting hotel by ID:', error);
      return null;
    }
  }

  /**
   * Get all hotels
   */
  static async getAllHotels(): Promise<Hotel[]> {
    try {
      const snapshot = await getDocs(query(getHotelsCollection(), orderBy('createdAt', 'desc')));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Hotel));
    } catch (error) {
      console.error('Error getting all hotels:', error);
      return [];
    }
  }

  /**
   * Update hotel status
   */
  static async updateHotelStatus(hotelId: string, status: 'online' | 'offline'): Promise<void> {
    await updateDoc(doc(getHotelsCollection(), hotelId), {
      status: status,
      updatedAt: new Date()
    });
  }

  /**
   * Update hotel
   */
  static async updateHotel(hotelId: string, updates: Partial<Hotel>): Promise<void> {
    await updateDoc(doc(getHotelsCollection(), hotelId), {
      ...updates,
      updatedAt: new Date()
    });
  }

  /**
   * Delete hotel
   */
  static async deleteHotel(hotelId: string): Promise<void> {
    const batch = writeBatch(getDb());

    // Delete hotel document
    batch.delete(doc(getHotelsCollection(), hotelId));

    // Delete hotel code reference
    const hotel = await this.getHotelById(hotelId);
    if (hotel?.hotelCode) {
      batch.delete(doc(getHotelCodesCollection(), hotel.hotelCode));
    }

    // Delete all hotel users (subcollection)
    const usersSnapshot = await getDocs(collection(getHotelsCollection(), hotelId, 'users'));
    usersSnapshot.docs.forEach(userDoc => {
      batch.delete(userDoc.ref);
    });

    // Delete all hotel zones (subcollection)
    const zonesSnapshot = await getDocs(collection(getHotelsCollection(), hotelId, 'zones'));
    zonesSnapshot.docs.forEach(zoneDoc => {
      batch.delete(zoneDoc.ref);
    });

    await batch.commit();
  }
}

// ============= WEB USERS (SaaS Customers) =============

export class WebUserService {
  /**
   * Create a new web user (SaaS customer)
   */
  static async createWebUser(uid: string, userData: CreateWebUserRequest, hotelCode: string): Promise<WebUser> {
    // Store password as plain text for hotel backend sync
    const plainPassword = userData.hotelAdmin.password;

    const webUser: WebUser = {
      id: uid,
      profile: userData.profile,
      subscription: {
        plan: 'trial',
        status: 'inactive',              // Trial henüz başlamadı
        isTrialActivated: false,         // Trial aktive edilmedi
        createdAt: new Date(),
        updatedAt: new Date()
      },
      ownedHotels: [hotelCode],
      hotelAdmin: {
        username: userData.hotelAdmin.username,
        password: plainPassword, // Changed from hashedPassword to plain text
        createdAt: new Date()
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Remove undefined fields before sending to Firebase
    const cleanWebUser = JSON.parse(JSON.stringify(webUser));
    await setDoc(doc(getWebUsersCollection(), uid), cleanWebUser);
    return webUser;
  }

  /**
   * Get web user by ID
   */
  static async getWebUserById(uid: string): Promise<WebUser | null> {
    try {
      const userDoc = await getDoc(doc(getWebUsersCollection(), uid));
      
      if (!userDoc.exists()) {
        return null;
      }

      return { id: userDoc.id, ...userDoc.data() } as WebUser;
    } catch (error) {
      console.error('Error getting web user by ID:', error);
      return null;
    }
  }

  /**
   * Update web user
   */
  static async updateWebUser(uid: string, updates: Partial<WebUser>): Promise<void> {
    await updateDoc(doc(getWebUsersCollection(), uid), {
      ...updates,
      updatedAt: new Date()
    });
  }

  /**
   * Add hotel to user's owned hotels
   */
  static async addHotelToUser(uid: string, hotelCode: string): Promise<void> {
    const userDoc = await getDoc(doc(getWebUsersCollection(), uid));
    if (userDoc.exists()) {
      const userData = userDoc.data() as WebUser;
      const updatedHotels = [...userData.ownedHotels, hotelCode];
      
      await updateDoc(doc(getWebUsersCollection(), uid), {
        ownedHotels: updatedHotels,
        updatedAt: new Date()
      });
    }
  }
}

// ============= HOTEL USERS (Subcollection) =============

export class HotelUserService {
  /**
   * Create hotel user
   */
  static async createHotelUser(hotelId: string, userData: CreateHotelUserRequest): Promise<HotelUser> {
    // Store password as plain text for backend sync
    const plainPassword = userData.password;

    const userRef = doc(collection(getHotelsCollection(), hotelId, 'users'));
    const hotelUser: HotelUser = {
      id: userRef.id,
      username: userData.username,
      password: plainPassword, // Changed from hashedPassword to plain text
      email: userData.email,
      role: userData.role,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(userRef, hotelUser);
    return hotelUser;
  }

  /**
   * Get hotel users
   */
  static async getHotelUsers(hotelId: string): Promise<HotelUser[]> {
    try {
      const snapshot = await getDocs(collection(getHotelsCollection(), hotelId, 'users'));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HotelUser));
    } catch (error) {
      console.error('Error getting hotel users:', error);
      return [];
    }
  }

  /**
   * Get hotel user by username
   */
  static async getHotelUserByUsername(hotelId: string, username: string): Promise<HotelUser | null> {
    try {
      const q = query(
        collection(getHotelsCollection(), hotelId, 'users'), 
        where('username', '==', username)
      );
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as HotelUser;
    } catch (error) {
      console.error('Error getting hotel user by username:', error);
      return null;
    }
  }

  /**
   * Update hotel user last login
   */
  static async updateUserLastLogin(hotelId: string, userId: string): Promise<void> {
    await updateDoc(doc(getHotelsCollection(), hotelId, 'users', userId), {
      lastLogin: new Date(),
      updatedAt: new Date()
    });
  }
}

// ============= HOTEL ZONES (Subcollection) =============

export class HotelZoneService {
  /**
   * Create hotel zone
   */
  static async createHotelZone(hotelId: string, zoneData: Partial<HotelZone>): Promise<HotelZone> {
    const zoneRef = doc(collection(getHotelsCollection(), hotelId, 'zones'));
    const hotelZone: HotelZone = {
      id: zoneRef.id,
      name: zoneData.name || '',
      deviceName: zoneData.deviceName || '',
      outputChannel: zoneData.outputChannel || '',
      musicFolderPath: zoneData.musicFolderPath || '',
      backendZoneId: zoneData.backendZoneId,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(zoneRef, hotelZone);
    return hotelZone;
  }

  /**
   * Get hotel zones
   */
  static async getHotelZones(hotelId: string): Promise<HotelZone[]> {
    try {
      const snapshot = await getDocs(collection(getHotelsCollection(), hotelId, 'zones'));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as HotelZone));
    } catch (error) {
      console.error('Error getting hotel zones:', error);
      return [];
    }
  }
}

// ============= UTILITY FUNCTIONS =============

/**
 * Clear all collections (for development/testing)
 */
export async function clearAllCollections(): Promise<void> {
  console.warn('⚠️ CLEARING ALL FIREBASE COLLECTIONS ⚠️');
  
  const batch = writeBatch(getDb());
  
  // Clear hotels
  const hotelsSnapshot = await getDocs(getHotelsCollection());
  hotelsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  
  // Clear webUsers
  const webUsersSnapshot = await getDocs(getWebUsersCollection());
  webUsersSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  
  // Clear hotelCodes
  const codesSnapshot = await getDocs(getHotelCodesCollection());
  codesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
  
  await batch.commit();
  console.log('✅ All collections cleared');
}

// ============= SUBSCRIPTION SERVICES =============

export class SubscriptionService {
  /**
   * Create trial subscription for new user
   */
  static async createTrialSubscription(webUserId: string): Promise<Subscription> {
    const subscriptionRef = doc(getSubscriptionsCollection());
    const now = new Date();
    const trialEnd = new Date(now.getTime() + 2 * 60 * 1000); // 2 minutes for testing
    
    const subscription: Subscription = {
      id: subscriptionRef.id,
      webUserId,
      plan: 'trial',
      status: 'active',
      trialEndsAt: trialEnd,
      currentPeriodStart: now,
      currentPeriodEnd: trialEnd,
      cancelAtPeriodEnd: false,
      createdAt: now,
      updatedAt: now
    };

    await setDoc(subscriptionRef, subscription);
    
    // Log trial start in billing history
    await this.logBillingEvent(subscription.id, webUserId, 'trial_start', '7 günlük deneme süresi başladı');
    
    return subscription;
  }

  /**
   * Get subscription by webUserId
   */
  static async getSubscriptionByUserId(webUserId: string): Promise<Subscription | null> {
    try {
      const q = query(getSubscriptionsCollection(), where('webUserId', '==', webUserId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Subscription;
    } catch (error) {
      console.error('Error getting subscription by user ID:', error);
      return null;
    }
  }

  /**
   * Check subscription status by hotel code
   */
  static async checkSubscriptionByHotelCode(hotelCode: string): Promise<{ status: 'active' | 'expired' | 'past_due' | 'not_found', daysLeft?: number }> {
    try {
      // Find hotel by code
      const hotel = await HotelService.getHotelByCode(hotelCode);
      if (!hotel) {
        return { status: 'not_found' };
      }

      // Find webUser who owns this hotel
      const q = query(getWebUsersCollection(), where('ownedHotels', 'array-contains', hotelCode));
      const userSnapshot = await getDocs(q);
      
      if (userSnapshot.empty) {
        return { status: 'not_found' };
      }

      const webUser = userSnapshot.docs[0].data() as WebUser;
      
      // Get subscription
      const subscription = await this.getSubscriptionByUserId(webUser.id);
      if (!subscription) {
        return { status: 'not_found' };
      }

      const now = new Date();
      const endDate = subscription.plan === 'trial' ? subscription.trialEndsAt : subscription.currentPeriodEnd;
      
      if (!endDate) {
        return { status: 'expired' };
      }

      if (endDate > now) {
        const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        return { status: 'active', daysLeft };
      } else {
        // Update subscription status to expired
        await updateDoc(doc(getSubscriptionsCollection(), subscription.id), {
          status: 'expired',
          updatedAt: now
        });
        
        // Log trial/subscription end
        await this.logBillingEvent(subscription.id, webUser.id, 'trial_end', 'Deneme süresi sona erdi');
        
        return { status: 'expired' };
      }
    } catch (error) {
      console.error('Error checking subscription by hotel code:', error);
      return { status: 'not_found' };
    }
  }

  /**
   * Update subscription to paid
   */
  static async upgradeToPaidSubscription(subscriptionId: string, stripeSubscriptionId: string, stripeCustomerId: string): Promise<void> {
    const now = new Date();
    const nextPeriodEnd = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days

    await updateDoc(doc(getSubscriptionsCollection(), subscriptionId), {
      plan: 'paid',
      status: 'active',
      currentPeriodStart: now,
      currentPeriodEnd: nextPeriodEnd,
      stripeSubscriptionId,
      stripeCustomerId,
      trialEndsAt: null, // Clear trial date
      updatedAt: now
    });
  }

  /**
   * Log billing event
   */
  static async logBillingEvent(subscriptionId: string, webUserId: string, type: BillingHistory['type'], description: string, amount?: number): Promise<void> {
    const historyRef = doc(getBillingHistoryCollection());
    
    const billingHistory: BillingHistory = {
      id: historyRef.id,
      subscriptionId,
      webUserId,
      type,
      description,
      amount,
      currency: amount ? 'TRY' : undefined,
      createdAt: new Date()
    };

    await setDoc(historyRef, billingHistory);
  }
}

// ============= PAYMENT SERVICES =============

export class PaymentService {
  /**
   * Create payment record
   */
  static async createPayment(subscriptionId: string, webUserId: string, amount: number, stripePaymentIntentId: string): Promise<Payment> {
    const paymentRef = doc(getPaymentsCollection());
    
    const payment: Payment = {
      id: paymentRef.id,
      subscriptionId,
      webUserId,
      amount,
      currency: 'TRY',
      status: 'pending',
      stripePaymentIntentId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(paymentRef, payment);
    return payment;
  }

  /**
   * Update payment status
   */
  static async updatePaymentStatus(paymentId: string, status: Payment['status'], failureReason?: string): Promise<void> {
    const updates: any = {
      status,
      updatedAt: new Date()
    };

    if (failureReason) {
      updates.failureReason = failureReason;
    }

    await updateDoc(doc(getPaymentsCollection(), paymentId), updates);
  }
}

/**
 * Initialize with sample data (for development)
 */
export async function initializeSampleData(): Promise<void> {
  console.log('🔄 Initializing sample data...');
  
  // Create admin hotel
  const adminHotelCode = await HotelCodeService.generateUniqueCode();
  await HotelCodeService.reserveCode(adminHotelCode);
  
  const adminHotel = await HotelService.createHotel({
    name: 'Sespresso Admin',
    address: 'Admin Server',
    serverUrl: '91.151.83.210',
    serverPort: 5252,
    isAdminInstance: true
  }, adminHotelCode);
  
  console.log(`✅ Admin hotel created: ${adminHotel.name} (Code: ${adminHotelCode})`);
}